/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type Store from "@ember-data/store";
import { action } from "@ember/object";
import type RouterService from "@ember/routing/router-service";
import { service } from "@ember/service";
import Component from "@glimmer/component";
import { cached, tracked } from "@glimmer/tracking";
import incrementalList from "parog-web/helpers/incremental-list";
import type BioExamResultsReceivedJE from "parog-web/models/bio-exam-results-received-journal-event";
import type BioExamObservation from "parog-web/models/loinc";
import type { Specimen as SpecimenCode } from "parog-web/models/loinc";
import type Patient from "parog-web/models/patient";
import type SemeiaSessionService from "parog-web/services/session";
import { sortBy } from "parog-web/utils/sort";
import triggerFileDownload from "parog-web/utils/trigger-file-download";
import trimLoincCode from "parog-web/utils/trim-loinc-code";
import styles from "./index.module.css";
import type { S as RowS } from "./row";
import { CellBioExamResult, referencesHookClass } from "./row/cell";

interface Args extends Pick<RowS["Args"], "activeReferencesHook"> {
  isLoading?: unknown;
  hideAction?: unknown;
  patient: Patient;
}

interface S {
  Args: Args;
}

class Specimen {
  constructor(public code: SpecimenCode) {}

  get label() {
    if (this.code == "BLD") return `bioExamResults.blood`;
    if (this.code == "UR") return "bioExamResults.urinary";

    return "bioExamResults.other";
  }
}

class Chapter {
  constructor(public label: string) {}
}

class LoincWithResults {
  constructor(
    public loinc: BioExamObservation,
    public results: CellBioExamResult[]
  ) {}

  get label() {
    return this.loinc.loinc;
  }
}

type LoincRow = Specimen | Chapter | LoincWithResults;

export default class BioExamResultsSummaryComponent extends Component<S> {
  styles = styles;

  @service declare router: RouterService;
  @service declare store: Store;
  @service declare session: SemeiaSessionService;

  incrementalList = incrementalList;

  @tracked activeReferencesHook?: string;

  showReferences = (event: Event) => {
    this.activeReferencesHook = (event.target as HTMLElement).closest(
      `.${referencesHookClass}`
    )?.id;
  };

  isSpecimen = (item: LoincRow): item is Specimen => {
    return item instanceof Specimen;
  };

  isChapter = (item: LoincRow): item is Chapter => {
    return item instanceof Chapter;
  };

  isLoincWithResults = (item: LoincRow): item is LoincWithResults => {
    return !this.isSpecimen(item) && !this.isChapter(item);
  };

  @cached
  get bioExamResultsReceived(): BioExamResultsReceivedJE[] {
    return (
      this.args.patient.bioExamResultsReceivedJournalEvents.sort(
        sortBy("occurredAt", "desc")
      ) ?? []
    );
  }

  sortedLoinc() {
    const specimenPriority = (specimen: BioExamObservation["specimen"]) => {
      if (specimen == "BLD") return 0;
      if (specimen == "UR") return 1;

      // all the other values
      return 2;
    };

    // @ts-expect-error the return type of this.store.peekAll provided by @types/ember-data is not correct
    const loincs = this.store.peekAll("loinc") as BioExamObservation[];

    return loincs.toSorted(function (bioA, bioB) {
      const specimenA = specimenPriority(bioA.specimen);
      const specimenB = specimenPriority(bioB.specimen);

      // Sort first by specimen, then by rank
      return specimenA !== specimenB
        ? specimenA - specimenB
        : bioA.rank - bioB.rank;
    });
  }

  @cached
  get loincRows(): LoincRow[] {
    const result: LoincRow[] = [];

    const seenSpecimens = new Set<SpecimenCode>();
    const seenChapters = new Set<string>();

    this.sortedLoinc().forEach(observation => {
      const { specimen, loincChapterLabel } = observation;

      /*
      Get all the results for a given LOINC.
      Each result will be associated to the JE it comes from
      */
      const resultsPerLoinc = this.bioExamResultsReceived.map(je => {
        const result = je.results.find(
          ({ code }) => trimLoincCode(code) === observation.loinc
        );
        return new CellBioExamResult(result, je);
      });

      // If there are not results matching this LOINC, early return
      if (
        resultsPerLoinc.filter(cellResult => Boolean(cellResult.result))
          .length == 0
      )
        return;

      if (specimen && !seenSpecimens.has(specimen)) {
        seenSpecimens.add(specimen);
        result.push(new Specimen(specimen));
      }

      const chapterId = `${specimen ?? "other"}-${loincChapterLabel}`;
      if (loincChapterLabel && !seenChapters.has(chapterId)) {
        seenChapters.add(chapterId);
        result.push(new Chapter(loincChapterLabel));
      }

      result.push(new LoincWithResults(observation, resultsPerLoinc));
    });

    return result;
  }

  @action
  downloadSummary() {
    const dates = [""];
    const rows: string[][] = [];

    document
      .querySelectorAll(
        "#bio-summary-table > thead > tr > th[data-test-column]"
      )
      .forEach(thElement => {
        dates.push(
          (thElement as HTMLTableCellElement).innerText.split("\n")[0]
        );
      });

    rows.push(dates);

    let group = "";

    document.querySelectorAll("table > tbody > tr").forEach(trElement => {
      const rowContent: string[] = [];
      // Get the LOINC group value from the tr element
      if (trElement.attributes.getNamedItem("data-test-group")) {
        group = (trElement.attributes.getNamedItem("data-test-group") as Attr)
          .value;
      }
      // Get the LOINC chapter from the tr element
      if (trElement.attributes.getNamedItem("data-test-loinc-chapter-row")) {
        let loincChapter = (
          trElement.children[0] as HTMLTableCellElement
        ).innerText.trim();

        // Append the LOINC group to the LOINC Chapter
        switch (group) {
          // Ex : Hématologie sanguine
          case "blood":
            loincChapter += " sanguine";
            break;
          // Ex : Biochimie urinaire
          case "urinary":
            loincChapter += " urinaire";
            break;
          // Ex : Autres réultats - Fertilité
          default:
            loincChapter = `Autres résultats - ${loincChapter}`;
        }
        rowContent.push(loincChapter);
      }
      // Get the result value & unit from the tr element
      if (trElement.attributes.getNamedItem("data-test-results-row")) {
        const children = trElement.children;
        for (let i = 0; i < children.length; i++) {
          const child = children.item(i) as HTMLTableCellElement;
          rowContent.push(child.innerText.replace("\n", " ").trim());
        }
      }

      if (rowContent.length) rows.push(rowContent);
    });

    triggerFileDownload({
      fileContent:
        "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n"),
      fileName: `historique_bilans_biologie_patient_${this.args.patient.fullName.replace(
        " ",
        "_"
      )}.csv`
    });
  }
}
